/**
 * Utilization Analysis - Pure analysis phase for utilization optimization
 *
 * This module performs capacity analysis to determine if and how to optimize
 * DRF utilization without modifying any candidate data.
 */

import { DRF_LIMITS } from '~/config/financial-constants';
import {
  calculateCompoundInterestCapacity,
  calculateExpectedInterest,
  calculateInterestAdjustedCapacity,
  calculateProportionalDistribution,
} from '~/engine/processing/calculators/analysis/capacity-analysis-calculator';
import { calculateYearsBetween } from '~/engine/processing/calculators/core/time-calculations';
import {
  calculateActualAdjustment,
  calculateDrfSurplus,
} from '~/engine/processing/calculators/metrics/optimization-metrics-calculator';
import { BondCategorizationEngine } from '~/engine/processing/shared/bond-categorization';
import type { Candidate, Configuration } from '~/types';

/**
 * Represents capacity information for a specific bond series in a payment period
 */
export interface BondSeriesCapacity {
  bondSeriesId: string;
  isActive: boolean;
  interestRate: number;
  maxBackwardInterestCapacity: number;
  principalToAdd: number; // Populated when an adjustment is selected
}

/**
 * Represents analysis data for a single cash flow period
 */
export interface CashFlowAdjustment {
  paymentDate: string;
  periodIndex: number;
  isInterestOnly: boolean;
  originalDrfEndingBalance: number;
  bondSeriesCapacities: BondSeriesCapacity[];
  minForwardDrfCapacity: number; // Min DRF in all future periods minus minimum balance
}

/**
 * Result of optimization analysis
 */
export interface OptimizationAnalysis {
  canOptimize: boolean;
  reason: string;
  targetPeriodIndex?: number;
  targetPaymentDate?: string;
  adjustments?: Array<{
    bondSeriesId: string;
    principalToAdd: number;
  }>;
  totalPrincipalToAdd?: number;
  expectedInterestImpact?: number;
}

/**
 * Configuration for utilization analysis
 */
export interface UtilizationAnalysisConfig {
  drfBufferThreshold?: number; // Default: 5000
  minPrincipalIncrement?: number; // Default: 5000
}

export class UtilizationAnalyzer {
  private config: UtilizationAnalysisConfig;

  constructor(config: UtilizationAnalysisConfig = {}) {
    this.config = {
      drfBufferThreshold: DRF_LIMITS.DRF_BUFFER_THRESHOLD,
      minPrincipalIncrement: 5000,
      ...config,
    };
  }

  /**
   * Analyzes a candidate to determine optimization opportunities
   */
  analyzeCandidate(candidate: Candidate, configuration: Configuration): OptimizationAnalysis {
    // Step 1: Check basic eligibility
    const eligibility = this.checkEligibility(candidate, configuration);
    if (!eligibility.isEligible) {
      return {
        canOptimize: false,
        reason: eligibility.reason,
      };
    }

    // Step 2: Build capacity analysis for all periods
    const capacityAnalysis = this.buildCapacityAnalysis(candidate, configuration);

    // Step 3: Find the best optimization opportunity
    const bestOpportunity = this.findBestOpportunity(
      candidate,
      capacityAnalysis,
      eligibility.drfBuffer
    );

    if (!bestOpportunity) {
      return {
        canOptimize: false,
        reason: 'No viable optimization opportunities found',
      };
    }

    return {
      canOptimize: true,
      reason: 'Optimization opportunity identified',
      targetPeriodIndex: bestOpportunity.periodIndex,
      targetPaymentDate: bestOpportunity.paymentDate,
      adjustments: bestOpportunity.adjustments,
      totalPrincipalToAdd: bestOpportunity.totalPrincipal,
      expectedInterestImpact: bestOpportunity.expectedInterest,
    };
  }

  /**
   * Checks if candidate is eligible for optimization
   */
  private checkEligibility(
    candidate: Candidate,
    configuration: Configuration
  ): {
    isEligible: boolean;
    reason: string;
    drfBuffer: number;
  } {
    // Check if candidate is viable
    if (!candidate.results?.isViable) {
      return {
        isEligible: false,
        reason: 'Candidate is not viable',
        drfBuffer: 0,
      };
    }

    // Check if there are any flexible bonds to optimize
    if (!BondCategorizationEngine.candidateHasFlexibleBonds(candidate, configuration)) {
      return {
        isEligible: false,
        reason: 'No flexible bonds available for optimization (all bonds have fixed targets)',
        drfBuffer: 0,
      };
    }

    // Check if DRF ever falls below minimum
    const minimumBalance = parseFloat(configuration.drfInputs.minimumBalance) || 0;
    const drfBelowMinimum = candidate.cashFlows.some((cf) => cf.drfEndingBalance < minimumBalance);

    if (drfBelowMinimum) {
      return {
        isEligible: false,
        reason: 'DRF falls below minimum balance',
        drfBuffer: 0,
      };
    }

    // Calculate DRF buffer (ending - starting)
    const startingDrf = candidate.cashFlows[0].drfStartingBalance;
    const endingDrf = candidate.cashFlows[candidate.cashFlows.length - 1].drfEndingBalance;
    const drfBuffer = calculateDrfSurplus(endingDrf, startingDrf);

    // Check if buffer is sufficient
    if (drfBuffer <= this.config.drfBufferThreshold!) {
      return {
        isEligible: false,
        reason: `Insufficient DRF buffer ($${drfBuffer.toLocaleString()} <= $${this.config.drfBufferThreshold!.toLocaleString()})`,
        drfBuffer,
      };
    }

    return {
      isEligible: true,
      reason: 'Candidate eligible for optimization',
      drfBuffer,
    };
  }

  /**
   * Builds capacity analysis for all cash flow periods
   */
  private buildCapacityAnalysis(
    candidate: Candidate,
    configuration: Configuration
  ): CashFlowAdjustment[] {
    const { cashFlows } = candidate;

    // Get the list of flexible bond IDs that can accept additional principal
    const flexibleBondIds = BondCategorizationEngine.getCandidateFlexibleBondIds(
      candidate,
      configuration
    );

    // Step 1: Create initial adjustment records
    const adjustments: CashFlowAdjustment[] = cashFlows.map((cf, index) => {
      const bondSeriesCapacities: BondSeriesCapacity[] = cf.bondPayments.map((bp) => {
        return {
          bondSeriesId: bp.bondSeriesId,
          isActive: bp.isActive,
          interestRate: bp.interestRate,
          maxBackwardInterestCapacity: 0, // Will be calculated
          principalToAdd: 0,
        };
      });

      return {
        paymentDate: cf.paymentDate,
        periodIndex: index,
        isInterestOnly: cf.isInterestOnly,
        originalDrfEndingBalance: cf.drfEndingBalance,
        bondSeriesCapacities,
        minForwardDrfCapacity: 0, // Will be calculated
      };
    });

    // Step 2: Calculate minForwardDrfCapacity (reverse pass)
    let minDrfInFuture = Infinity;
    for (let i = adjustments.length - 1; i >= 0; i--) {
      const currentDrf = adjustments[i].originalDrfEndingBalance;
      minDrfInFuture = Math.min(minDrfInFuture, currentDrf);

      // Capacity is the minimum future DRF minus the minimum balance requirement
      adjustments[i].minForwardDrfCapacity = Math.max(
        0,
        minDrfInFuture - (parseFloat(configuration.drfInputs.minimumBalance) || 0)
      );
    }

    // Step 3: Calculate maxBackwardInterestCapacity for each bond
    for (let i = 0; i < adjustments.length; i++) {
      const adjustment = adjustments[i];

      for (const bondCapacity of adjustment.bondSeriesCapacities) {
        bondCapacity.maxBackwardInterestCapacity = this.calculateMaxBackwardInterestCapacity(
          i,
          bondCapacity.bondSeriesId,
          adjustments,
          flexibleBondIds
        );
      }
    }

    return adjustments;
  }

  /**
   * Calculates how much new interest from principal additions can be absorbed by prior periods
   */
  private calculateMaxBackwardInterestCapacity(
    periodIndex: number,
    bondSeriesId: string,
    allAdjustments: CashFlowAdjustment[],
    flexibleBondIds: string[]
  ): number {
    // Check if this bond is flexible (can accept additional principal)
    if (!flexibleBondIds.includes(bondSeriesId)) {
      return 0; // Fixed target bonds have no capacity for optimization
    }

    const currentPeriod = allAdjustments[periodIndex];
    const currentBondInfo = currentPeriod.bondSeriesCapacities.find(
      (bc) => bc.bondSeriesId === bondSeriesId
    );

    if (!currentBondInfo?.isActive) {
      return 0;
    }

    // For the first period, capacity is just the forward DRF capacity
    if (periodIndex === 0) {
      return Math.round(currentPeriod.minForwardDrfCapacity / (1 + currentBondInfo.interestRate));
    }

    // Get prior period bond information
    const priorPeriod = allAdjustments[periodIndex - 1];
    const priorBondInfo = priorPeriod.bondSeriesCapacities.find(
      (bc) => bc.bondSeriesId === bondSeriesId
    );

    if (!priorBondInfo?.isActive) {
      // Bond wasn't active in prior period, so capacity is current period's DRF capacity
      return Math.round(currentPeriod.minForwardDrfCapacity / (1 + currentBondInfo.interestRate));
    }

    // Both current and prior periods have active bond
    // Capacity is minimum of:
    // 1. Current period's DRF capacity (adjusted for interest)
    // 2. Prior period's backward interest capacity (adjusted for compounding)
    const currentPeriodCapacity = calculateInterestAdjustedCapacity(
      currentPeriod.minForwardDrfCapacity,
      currentBondInfo.interestRate
    );
    const priorPeriodCapacity = calculateInterestAdjustedCapacity(
      priorBondInfo.maxBackwardInterestCapacity,
      currentBondInfo.interestRate
    );

    return Math.round(Math.min(currentPeriodCapacity, priorPeriodCapacity));
  }

  /**
   * Finds the best optimization opportunity from capacity analysis
   */
  private findBestOpportunity(
    candidate: Candidate,
    capacityAnalysis: CashFlowAdjustment[],
    targetAdjustment: number
  ): {
    periodIndex: number;
    paymentDate: string;
    adjustments: Array<{ bondSeriesId: string; principalToAdd: number }>;
    totalPrincipal: number;
    expectedInterest: number;
  } | null {
    // Try each period to find the best opportunity
    for (let i = 0; i < capacityAnalysis.length; i++) {
      const adjustment = capacityAnalysis[i];

      // Skip interest-only periods
      if (adjustment.isInterestOnly) continue;

      // Find active bonds with capacity
      const activeBonds = adjustment.bondSeriesCapacities.filter(
        (bc) => bc.isActive && bc.maxBackwardInterestCapacity > 0
      );

      if (activeBonds.length === 0) continue;

      // Calculate total available capacity
      const minBackwardCapacity = Math.min(
        ...activeBonds.map((bc) => bc.maxBackwardInterestCapacity)
      );

      const totalCapacity = calculateActualAdjustment(
        Math.min(adjustment.minForwardDrfCapacity, minBackwardCapacity),
        targetAdjustment
      );

      if (totalCapacity < this.config.minPrincipalIncrement!) continue;

      // Calculate how much principal we can add considering interest impact
      const maxInterestRate = Math.max(...activeBonds.map((bc) => bc.interestRate));
      const startingTaxYear = candidate.cashFlows[0].relatedTaxBillYear;
      const currentTaxYear = candidate.cashFlows[i].relatedTaxBillYear;
      const yearsBetween = calculateYearsBetween(currentTaxYear, startingTaxYear);

      const principalCapacity = calculateCompoundInterestCapacity(
        totalCapacity,
        maxInterestRate,
        yearsBetween
      );

      // Round down to increment
      const principalToAdd =
        Math.floor(principalCapacity / this.config.minPrincipalIncrement!) *
        this.config.minPrincipalIncrement!;

      if (principalToAdd < this.config.minPrincipalIncrement!) continue;

      // Distribute principal proportionally among active bonds
      const totalBondCapacity = activeBonds.reduce(
        (sum, bc) => sum + bc.maxBackwardInterestCapacity,
        0
      );

      const adjustments = activeBonds
        .map((bc) => {
          const proportion = calculateProportionalDistribution(
            bc.maxBackwardInterestCapacity,
            totalBondCapacity,
            1
          );
          const bondPrincipal =
            Math.floor((principalToAdd * proportion) / this.config.minPrincipalIncrement!) *
            this.config.minPrincipalIncrement!;

          return {
            bondSeriesId: bc.bondSeriesId,
            principalToAdd: bondPrincipal,
          };
        })
        .filter((adj) => adj.principalToAdd > 0);

      if (adjustments.length === 0) continue;

      // Calculate total principal and expected interest impact
      const totalPrincipal = adjustments.reduce((sum, adj) => sum + adj.principalToAdd, 0);
      const expectedInterest = calculateExpectedInterest(
        totalPrincipal,
        maxInterestRate,
        yearsBetween
      );

      return {
        periodIndex: i,
        paymentDate: adjustment.paymentDate,
        adjustments,
        totalPrincipal,
        expectedInterest,
      };
    }

    return null;
  }
}
